import React, { useState, useEffect } from 'react';
import './DashboardAnimations.css';
import {
    BarChart2,
    PieChart,
    LineChart,
    TrendingUp,
    Users,
    ShoppingCart,
    DollarSign,
    ArrowUp,
    ArrowDown,
    Percent,
    Clock,
    RefreshCw,
    Calendar,
    Map,
    Globe,
    Filter,
    Search,
    Download,
    Flag,
    Share2,
    Bell,
    ChevronRight
} from 'lucide-react';

const AnalyticsDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedPeriod, setSelectedPeriod] = useState('week');

    // Simulate data loading
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        setTimeout(() => {
            setRefreshing(false);
        }, 1000);
    };

    return (
        <div className="flex justify-center items-center p-4 bg-slate-950 min-h-screen">
            {/* Main container with max-width to prevent stretching */}
            <div className="w-full max-w-6xl mx-auto">
                <div className="relative bg-slate-900 rounded-xl shadow-2xl border border-slate-800 overflow-hidden">
                    {/* Analytics-themed header with graph motif */}
                    <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500"></div>

                        {/* Graph pattern decorative elements */}
                        <div className="absolute inset-0 opacity-10">
                            {/* Simulated graph lines */}
                            <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none">
                                <path 
                                    d="M0 35 L10 32 L20 34 L30 28 L40 29 L50 25 L60 28 L70 24 L80 20 L90 15 L100 10" 
                                    fill="none" 
                                    stroke="white" 
                                    strokeWidth="0.5"
                                />
                                <path 
                                    d="M0 38 L10 36 L20 38 L30 32 L40 30 L50 32 L60 30 L70 28 L80 26 L90 20 L100 18" 
                                    fill="none" 
                                    stroke="white" 
                                    strokeWidth="0.5"
                                />
                            </svg>
                        </div>

                        <div className="flex items-center justify-between px-6 py-4 relative z-10">
                            <div className="flex items-center space-x-4">
                                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
                                    <BarChart2 className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Analytics Dashboard</h1>
                                    <p className="text-slate-400 text-sm">Business performance insights</p>
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button onClick={handleRefresh} className="flex items-center space-x-1 text-slate-300 hover:text-white">
                                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                    <span className="text-sm">Refresh</span>
                                </button>

                                <div className="hidden md:flex items-center space-x-2">
                                    <button className={`px-3 py-1 text-xs rounded-full ${selectedPeriod === 'day' ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`} onClick={() => setSelectedPeriod('day')}>
                                        Day
                                    </button>
                                    <button className={`px-3 py-1 text-xs rounded-full ${selectedPeriod === 'week' ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`} onClick={() => setSelectedPeriod('week')}>
                                        Week
                                    </button>
                                    <button className={`px-3 py-1 text-xs rounded-full ${selectedPeriod === 'month' ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`} onClick={() => setSelectedPeriod('month')}>
                                        Month
                                    </button>
                                </div>

                                <div className="hidden md:flex items-center space-x-1 text-slate-400">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">Last updated: <span className="text-slate-300">5m ago</span></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dashboard tabs */}
                    <div className="flex items-center px-6 py-2 bg-slate-800/50 border-b border-slate-700/50 overflow-x-auto">
                        <div className="flex space-x-1">
                            {['overview', 'customers', 'revenue', 'products'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 rounded-t-lg text-sm font-medium capitalize transition-colors ${activeTab === tab
                                        ? 'bg-slate-800 text-white border-t border-l border-r border-slate-600/50'
                                        : 'text-slate-400 hover:text-slate-300'
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className="ml-auto flex items-center space-x-3">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-500" />
                                <input 
                                    type="text" 
                                    placeholder="Search metrics..." 
                                    className="py-2 pl-8 pr-4 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 w-48"
                                />
                            </div>
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Filter className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700">
                                <Download className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Main dashboard content */}
                    <div className="p-6">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-96">
                                <div className="relative">
                                    <BarChart2 className="w-12 h-12 text-slate-600 animate-pulse" />
                                    <div className="absolute w-full h-full top-0 left-0 flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Top metrics cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                    {[
                                        {
                                            title: 'Total Revenue',
                                            value: '$48,294',
                                            trend: 'up',
                                            change: '12.5%',
                                            icon: <DollarSign className="w-5 h-5" />,
                                            color: 'purple'
                                        },
                                        {
                                            title: 'Conversions',
                                            value: '537',
                                            trend: 'up',
                                            change: '8.2%',
                                            icon: <TrendingUp className="w-5 h-5" />,
                                            color: 'pink'
                                        },
                                        {
                                            title: 'Avg. Order Value',
                                            value: '$89.92',
                                            trend: 'down',
                                            change: '3.1%',
                                            icon: <ShoppingCart className="w-5 h-5" />,
                                            color: 'indigo'
                                        },
                                        {
                                            title: 'Active Users',
                                            value: '3,842',
                                            trend: 'up',
                                            change: '4.6%',
                                            icon: <Users className="w-5 h-5" />,
                                            color: 'blue'
                                        }
                                    ].map((metric, index) => (
                                        <div
                                            key={index}
                                            className="relative bg-slate-800 rounded-xl p-4 border border-slate-700 overflow-hidden"
                                        >
                                            {/* Decorative accent */}
                                            <div className={`absolute top-0 left-0 w-full h-1 bg-${metric.color}-500`}></div>

                                            <div className="relative z-10">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className={`p-2 rounded-lg bg-${metric.color}-500/10 text-${metric.color}-500`}>
                                                        {metric.icon}
                                                    </div>
                                                    <div className={`flex items-center text-${metric.trend === 'up' ? 'green' : 'red'}-400 text-sm`}>
                                                        {metric.trend === 'up' ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
                                                        {metric.change}
                                                    </div>
                                                </div>

                                                <div className="mb-1 text-slate-400 text-sm">{metric.title}</div>
                                                <div className="text-2xl font-bold text-white">{metric.value}</div>
                                            </div>

                                            {/* Decorative gradient */}
                                            <div className={`absolute bottom-0 right-0 w-24 h-24 bg-${metric.color}-500/5 rounded-full blur-2xl -mb-8 -mr-8`}></div>
                                        </div>
                                    ))}
                                </div>

                                {/* Main charts grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                                    {/* Large chart - Revenue Trends */}
                                    <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
                                            <div>
                                                <h3 className="font-medium text-white">Revenue Trends</h3>
                                                <p className="text-slate-400 text-sm">Weekly performance analysis</p>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button className="p-1 text-purple-500 hover:bg-slate-700 rounded">
                                                    <LineChart className="w-5 h-5" />
                                                </button>
                                                <button className="p-1 text-slate-400 hover:bg-slate-700 hover:text-white rounded">
                                                    <BarChart2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="px-4 py-4">
                                            {/* Chart visualization - this would be replaced with an actual chart component */}
                                            <div className="h-72 w-full relative overflow-hidden">
                                                {/* Simulated chart visualization */}
                                                <div className="absolute bottom-0 left-0 right-0 h-64 flex items-end">
                                                    {/* X-axis */}
                                                    <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-700"></div>

                                                    {/* Y-axis */}
                                                    <div className="absolute top-0 bottom-0 left-0 w-px bg-slate-700"></div>

                                                    {/* Y-axis labels */}
                                                    {[0, 10000, 20000, 30000, 40000, 50000].map((value, i) => (
                                                        <div key={i} className="absolute left-0 text-xs text-slate-500" style={{ bottom: `${(value / 50000) * 64}px` }}>
                                                            <span className="block w-14 text-right mr-2">${(value/1000).toFixed(0)}k</span>
                                                            <div className="absolute left-16 right-0 h-px bg-slate-700/30" style={{ opacity: value === 0 ? 0 : 1 }}></div>
                                                        </div>
                                                    ))}

                                                    {/* Simulated line chart for revenue */}
                                                    <div className="relative flex-1 flex items-end pl-16">
                                                        <div className="relative w-full h-full">
                                                            <svg className="w-full h-full" viewBox="0 0 100 64" preserveAspectRatio="none">
                                                                <linearGradient id="gradientAnalytics" x1="0%" y1="0%" x2="0%" y2="100%">
                                                                    <stop offset="0%" stopColor="rgba(168, 85, 247, 0.5)" />
                                                                    <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
                                                                </linearGradient>
                                                                <path
                                                                    d="M0 50 L10 45 L20 48 L30 40 L40 42 L50 30 L60 25 L70 28 L80 20 L90 15 L100 10 V64 H0 Z"
                                                                    fill="url(#gradientAnalytics)"
                                                                    className="animate-fade-in"
                                                                    style={{ animationDelay: '0.5s' }}
                                                                />
                                                                <path
                                                                    d="M0 50 L10 45 L20 48 L30 40 L40 42 L50 30 L60 25 L70 28 L80 20 L90 15 L100 10"
                                                                    fill="none"
                                                                    stroke="#A855F7"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    className="animate-draw-line"
                                                                    style={{ animationDuration: '2s' }}
                                                                />
                                                            </svg>
                                                        </div>
                                                    </div>

                                                    {/* X-axis labels */}
                                                    <div className="absolute bottom-0 left-16 right-4 flex justify-between text-xs text-slate-500 pb-6">
                                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                                                            <div key={i} className="text-center">
                                                                <div className="absolute bottom-6 w-px h-2 bg-slate-700" style={{ left: `${16 + (i * 12.5)}%` }}></div>
                                                                <span>{day}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right side chart - Conversion Rate */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Conversion Rate</h3>
                                            <p className="text-slate-400 text-sm">Performance by channel</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="flex justify-center items-center mb-4">
                                                {/* Simulated donut chart */}
                                                <div className="relative w-40 h-40">
                                                    <svg viewBox="0 0 100 100" className="w-full h-full">
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#1E293B" strokeWidth="12" />
                                                        
                                                        {/* Segments of the donut chart */}
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#A855F7" strokeWidth="12" strokeDasharray="125.6 125.6" strokeDashoffset="0" transform="rotate(-90 50 50)" className="animate-draw-circle" style={{ animationDelay: '0.2s', animationDuration: '1.5s' }} />
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#EC4899" strokeWidth="12" strokeDasharray="75.4 176.6" strokeDashoffset="0" transform="rotate(45 50 50)" className="animate-draw-circle" style={{ animationDelay: '0.4s', animationDuration: '1.5s' }} />
                                                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#3B82F6" strokeWidth="12" strokeDasharray="50.2 201.6" strokeDashoffset="0" transform="rotate(160 50 50)" className="animate-draw-circle" style={{ animationDelay: '0.6s', animationDuration: '1.5s' }} />
                                                    </svg>
                                                    
                                                    {/* Center text */}
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                        <div className="text-2xl font-bold text-white">4.8%</div>
                                                        <div className="text-xs text-slate-400">Avg. Rate</div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Legend */}
                                            <div className="space-y-2">
                                                {[
                                                    { name: 'Organic Search', value: '6.2%', color: 'purple' },
                                                    { name: 'Social Media', value: '3.8%', color: 'pink' },
                                                    { name: 'Direct Traffic', value: '4.5%', color: 'blue' }
                                                ].map((item, i) => (
                                                    <div key={i} className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className={`w-3 h-3 rounded-full bg-${item.color}-500 mr-2`}></div>
                                                            <span className="text-sm text-slate-300">{item.name}</span>
                                                        </div>
                                                        <span className="text-sm font-medium text-white">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom row - three equal cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Traffic Sources */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Traffic Sources</h3>
                                            <p className="text-slate-400 text-sm">Top referrers</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3">
                                                {[
                                                    { name: 'Organic Search', value: '42%', change: '+5.4%', color: 'emerald' },
                                                    { name: 'Social Media', value: '28%', change: '+2.1%', color: 'blue' },
                                                    { name: 'Direct Traffic', value: '16%', change: '-0.3%', color: 'amber' },
                                                    { name: 'Email Campaigns', value: '10%', change: '+1.5%', color: 'purple' },
                                                    { name: 'Referrals', value: '4%', change: '-0.8%', color: 'pink' }
                                                ].map((source, i) => (
                                                    <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-700/30">
                                                        <div className="flex items-center">
                                                            <div className={`w-8 h-2 rounded-full bg-${source.color}-500 mr-3`}></div>
                                                            <span className="text-sm text-slate-300">{source.name}</span>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <div className="text-sm font-medium text-white mr-2">{source.value}</div>
                                                            <div className={`text-xs ${source.change.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
                                                                {source.change}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Geographic Reach */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Geographic Reach</h3>
                                            <p className="text-slate-400 text-sm">Top countries by users</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    { country: 'United States', users: '1,845', percent: 48, flag: '🇺🇸' },
                                                    { country: 'United Kingdom', users: '683', percent: 18, flag: '🇬🇧' },
                                                    { country: 'Germany', users: '397', percent: 10, flag: '🇩🇪' },
                                                    { country: 'India', users: '245', percent: 6, flag: '🇮🇳' },
                                                    { country: 'Japan', users: '211', percent: 5, flag: '🇯🇵' },
                                                    { country: 'Australia', users: '176', percent: 4, flag: '🇦🇺' },
                                                    { country: 'Other', users: '325', percent: 9, flag: '🌐' }
                                                ].map((country, i) => (
                                                    <div key={i} className="flex items-center p-1.5">
                                                        <div className="text-xl mr-2">{country.flag}</div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between mb-1">
                                                                <span className="text-sm text-slate-300">{country.country}</span>
                                                                <span className="text-sm text-white">{country.users}</span>
                                                            </div>
                                                            <div className="h-1.5 w-full bg-slate-700 rounded-full overflow-hidden">
                                                                <div 
                                                                    className="h-full bg-purple-500 rounded-full"
                                                                    style={{ width: `${country.percent}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recent Transactions */}
                                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                        <div className="px-4 py-3 border-b border-slate-700">
                                            <h3 className="font-medium text-white">Recent Transactions</h3>
                                            <p className="text-slate-400 text-sm">Latest purchases</p>
                                        </div>
                                        <div className="px-4 py-4">
                                            <div className="space-y-3 max-h-[240px] overflow-y-auto">
                                                {[
                                                    { id: '#TRX-7291', amount: '$94.25', status: 'completed', time: '12m ago' },
                                                    { id: '#TRX-7290', amount: '$237.50', status: 'completed', time: '47m ago' },
                                                    { id: '#TRX-7289', amount: '$124.99', status: 'pending', time: '1h ago' },
                                                    { id: '#TRX-7288', amount: '$46.75', status: 'completed', time: '3h ago' },
                                                    { id: '#TRX-7287', amount: '$178.60', status: 'failed', time: '4h ago' },
                                                    { id: '#TRX-7286', amount: '$159.99', status: 'completed', time: '5h ago' }
                                                ].map((transaction, i) => (
                                                    <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-700/30">
                                                        <div>
                                                            <p className="text-sm font-medium text-slate-300">{transaction.id}</p>
                                                            <p className="text-xs text-slate-500">{transaction.time}</p>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-medium text-white mr-2">{transaction.amount}</span>
                                                            <span 
                                                                className={`px-2 py-0.5 text-xs rounded-full ${
                                                                    transaction.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                                                    transaction.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                                                    'bg-red-500/20 text-red-400'
                                                                }`}
                                                            >
                                                                {transaction.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Dashboard footer */}
                    <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700/50 flex items-center justify-between">
                        <div className="text-sm text-slate-400">
                            Showing data for <span className="text-slate-300 font-medium">{selectedPeriod === 'day' ? 'Today' : selectedPeriod === 'week' ? 'This Week' : 'This Month'}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-md flex items-center">
                                <Share2 className="w-3.5 h-3.5 mr-1.5" />
                                Share Report
                            </button>
                            <button className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-500 text-white rounded-md flex items-center">
                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                Export Data
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;